export interface PendingDepartment {
    name?: string;
    status?: string;
}

export interface PendingDeleteDept {
    awaitingName?: boolean;
}

export const defaultStatus = "active";

/** 
 * Main handler to start the creation process 
 */
export const startCreateDepartmentFlow = (
    setPendingDept: (data: PendingDepartment | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        // If user already said "create department named X"
        setPendingDept({ name: nameFromIntent, status: defaultStatus });

        pushAIChat(
            `Got it! Creating "${nameFromIntent}". Should I keep status as Active?`
        );
        speak(`Creating ${nameFromIntent}. Do you want status active?`);
        return;
    }

    // Ask user for name
    setPendingDept({});
    pushAIChat("Sure! What should the department name be?");
    speak("What should the department name be?");
};



/**
 * Multi-step flow handler for department creation.
 * This function continues the conversation until all fields are collected.
 */
export const processPendingDepartment = (
    text: string,
    pendingDept: PendingDepartment,
    setPendingDept: (data: PendingDepartment | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — If name is missing, take user's text as the name
    if (!pendingDept.name) {
        const cleanName = text.replace(/[^a-zA-Z0-9 ]/g, "").trim();

        if (!cleanName) {
            pushAIChat("Please provide a valid department name.");
            speak("Please say a valid name.");
            return;
        }

        setPendingDept({ ...pendingDept, name: cleanName });

        pushAIChat(`Great! Should "${cleanName}" be Active or Inactive?`);
        speak(`Should ${cleanName} be active or inactive?`);
        return;
    }

    // STEP 2 — Capture status
    // STEP 2 — Capture status
    if (!pendingDept.status) {
        const status = text.includes("inactive") ? "inactive" : "active";

        setPendingDept({ ...pendingDept, status });

        pushAIChat(`Perfect! Creating "${pendingDept.name}" with status ${status}...`);
        speak(`Creating ${pendingDept.name} with status ${status}`);

        // 👉 Trigger CREATE department
        window.dispatchEvent(
            new CustomEvent("ai-create-department", {
                detail: { name: pendingDept.name, status }
            })
        );

        setPendingDept(null);
        return;
    }

};


export const startDeleteDepartmentFlow = (
    setPendingDelete: (data: PendingDeleteDept | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    // If name already detected
    if (nameFromIntent) {
        pushAIChat(`Deleting department "${nameFromIntent}"...`);
        speak(`Deleting department ${nameFromIntent}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-department", {
                detail: { name: nameFromIntent }
            })
        );
        return;
    }

    // Otherwise ask user
    setPendingDelete({ awaitingName: true });

    pushAIChat("Sure! Which department do you want to delete?");
    speak("Which department do you want to delete?");
};

export const processPendingDeleteDepartment = (
    text: string,
    pendingDelete: PendingDeleteDept,
    setPendingDelete: (data: PendingDeleteDept | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (pendingDelete.awaitingName) {
        const cleanName = text.replace(/[^a-zA-Z0-9 ]/g, "").trim();

        if (!cleanName) {
            pushAIChat("Please say a valid department name.");
            speak("Please provide a valid department name.");
            return;
        }

        pushAIChat(`Deleting department "${cleanName}"...`);
        speak(`Deleting department ${cleanName}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-department", {
                detail: { name: cleanName }
            })
        );

        setPendingDelete(null);
        return;
    }
};


